import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    {
      name: 'Services',
      children: [
        { name: '1:1 Sessions', path: '/services/sessions' },
        { name: 'The Studio', path: '/services/studio' },
        { name: 'Equipment', path: '/services/equipment' },
        { name: 'Comprehensive Approach', path: '/services/comprehensive' },
      ],
    },
    { name: 'Pricing', path: '/pricing' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Bookings', path: '/bookings' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'glass-nav border-b border-stone-200 py-4' : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo">
            <h1 className="font-heading text-2xl md:text-3xl font-medium text-stone-900 tracking-tight">
              TEZR
            </h1>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) =>
              link.children ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`nav-${link.name.toLowerCase()}-dropdown`}
                      className="nav-link flex items-center gap-1"
                    >
                      {link.name}
                      <ChevronDown size={14} strokeWidth={1.5} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="bg-white border border-stone-200 shadow-lg rounded-none p-2"
                    align="start"
                  >
                    {link.children.map((child) => (
                      <DropdownMenuItem key={child.path} asChild>
                        <Link
                          to={child.path}
                          data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className="text-stone-600 hover:text-stone-900 cursor-pointer px-4 py-2 text-sm"
                        >
                          {child.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`nav-link ${
                    location.pathname === link.path ? 'text-stone-900' : ''
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
            
            {/* Instagram */}
            <a
              href="https://www.instagram.com/tezrpilates"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-instagram"
              className="text-stone-500 hover:text-stone-900 transition-colors"
            >
              <Instagram size={18} strokeWidth={1.5} />
            </a>

            {/* Book Now */}
            <Button
              asChild
              data-testid="nav-book-now"
              className="btn-primary rounded-none"
            >
              <Link to="/bookings">Book Now</Link>
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-stone-900 p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden fixed inset-0 top-[73px] bg-stone-50 z-40"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.name} className="space-y-3">
                    <span className="text-xs uppercase tracking-[0.2em] text-stone-400 font-medium">
                      {link.name}
                    </span>
                    <div className="pl-4 space-y-3 border-l border-stone-200">
                      {link.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          data-testid={`mobile-nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className="block text-stone-600 hover:text-stone-900 transition-colors"
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                    className={`text-lg font-heading ${
                      location.pathname === link.path ? 'text-stone-900' : 'text-stone-600'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              )}
              
              <div className="flex items-center gap-4 pt-6 border-t border-stone-200">
                <a
                  href="https://www.instagram.com/tezrpilates"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-nav-instagram"
                  className="text-stone-500 hover:text-stone-900 transition-colors"
                >
                  <Instagram size={24} strokeWidth={1.5} />
                </a>
                <Button
                  asChild
                  data-testid="mobile-nav-book-now"
                  className="flex-1 btn-primary rounded-none"
                >
                  <Link to="/bookings">Book Now</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
